import type { FC, ReactNode } from 'react'
import type { AnnouncementSettings, Category } from '@/types/marketplace'
import type { HeaderViewer } from '@/lib/auth/viewer'
import type { OwnerCta } from '@/lib/community/owner-cta'

import { cn } from '@/lib/utils'
import { MtaHeader } from '@/components/marketplace/mta-header'
import { SiteFooter } from '@/components/marketplace/site-footer'

interface SiteShellProps {
    children: ReactNode
    /**
     * `initialQuery`, `showSearch` and `disclosure` are still accepted so every
     * page keeps its existing call signature. The approved header carries no
     * search field and the footer renders its own legal row, so none of the
     * three are forwarded any more.
     */
    initialQuery?: string
    showSearch?: boolean
    disclosure?: string
    /** Omitted on pages that do not load settings. The bar simply stays hidden. */
    announcement?: AnnouncementSettings | null
    className?: string
    /** Accepted for backwards compatibility. The header is now always shared. */
    bareTop?: boolean
    /** Rendered above `main`, directly under the shared header. */
    top?: ReactNode
    /** Feeds the header's mobile shortcut list. Optional on every page. */
    categories?: Array<Category>
    /**
     * The header's platform action, resolved on the server for this visitor by
     * `getOwnerCta`. Omitted, the header shows START YOUR MARKETPLACE, which is
     * the right answer for a signed-out visitor and the previous behaviour
     * everywhere else.
     */
    ownerCta?: OwnerCta | null
    /**
     * The signed-in member, resolved on the server for this visitor by
     * `getHeaderState`. Omitted, the header carries no account menu, which is
     * the right answer for a signed-out visitor and the previous behaviour
     * everywhere else.
     */
    viewer?: HeaderViewer | null
}

export const SiteShell: FC<SiteShellProps> = ({
    children,
    announcement,
    className,
    top,
    categories,
    ownerCta,
    viewer
}): ReactNode => {
    return (
        <div className='flex min-h-screen flex-col bg-background'>
            <MtaHeader
                categories={categories}
                announcement={announcement?.enabled ? announcement.text : undefined}
                ownerCta={ownerCta}
                viewer={viewer}
            />

            {top}

            <main id='main' className={cn('flex-1', className)}>
                {children}
            </main>

            <SiteFooter />
        </div>
    )
}

interface SectionProps {
    id?: string
    className?: string
    children: ReactNode
}

/** Standard page gutter. Every section uses this so rhythm stays consistent. */
export const Section: FC<SectionProps> = ({ id, className, children }): ReactNode => {
    return (
        <section id={id} className={cn('mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8', className)}>
            {children}
        </section>
    )
}

interface SectionHeadingProps {
    title: string
    description?: string
    action?: ReactNode
    className?: string
}

export const SectionHeading: FC<SectionHeadingProps> = ({ title, description, action, className }): ReactNode => {
    return (
        <div className={cn('flex flex-wrap items-end justify-between gap-3', className)}>
            <div className='max-w-2xl'>
                <h2 className='type-display text-xl text-foreground sm:text-2xl'>
                    {title}
                </h2>

                {description && (
                    <p className='mt-1.5 text-sm text-muted-foreground'>
                        {description}
                    </p>
                )}
            </div>

            {action}
        </div>
    )
}
